import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { config } from '../config';

interface IncidentUpdate {
  id: string;
  state?: string;
  message: string;
  author?: string;
  created_at: string;
}

interface Incident {
  id: string;
  title: string;
  summary?: string;
  severity: string;
  state: string;
  started_at: string;
  resolved_at?: string;
  components?: Array<{ id: string; display_name: string }>;
  updates?: IncidentUpdate[];
}

export default function IncidentPage() {
  const { id } = useParams<{ id: string }>();
  const [incident, setIncident] = useState<Incident | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIncident();
  }, [id]);

  async function fetchIncident() {
    try {
      const apiUrl = config.apiUrl;
      const res = await fetch(`${apiUrl}/v1/stats/incidents/${id}`);

      if (res.ok) {
        const data = await res.json();
        setIncident(data);
      }
    } catch (error) {
      console.error('Failed to fetch incident', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="relative">
          <div className="w-10 h-10 border-2 border-gray-200 rounded-full" />
          <div className="absolute inset-0 w-10 h-10 border-2 border-transparent border-t-unlimit-lime rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-bold text-navy-900">Incident not found</h2>
        <Link to="/" className="text-sm text-navy-900 hover:text-unlimit-lime-dark mt-4 inline-block font-medium">
          Return to status page
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Link to="/" className="inline-flex items-center text-sm text-gray-500 hover:text-navy-900 transition-colors font-medium">
        <ArrowLeft className="h-4 w-4 mr-1.5" />
        Back to status
      </Link>

      <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-navy-900">{incident.title}</h1>
            {incident.summary && (
              <p className="text-sm text-gray-500 mt-1.5">{incident.summary}</p>
            )}
          </div>
          <span className={`px-2.5 py-1 text-xs font-semibold rounded-md ${
            incident.severity === 'CRITICAL' ? 'bg-rose-50 text-rose-400' :
            incident.severity === 'MAJOR' ? 'bg-amber-50 text-amber-500' :
            'bg-yellow-50 text-yellow-700'
          }`}>
            {incident.severity}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Status</span>
            <span className={`block mt-1 font-semibold ${
              incident.state === 'RESOLVED' ? 'text-emerald-600' :
              incident.state === 'MONITORING' ? 'text-blue-600' :
              'text-orange-600'
            }`}>
              {incident.state}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Started</span>
            <span className="block mt-1 font-medium text-navy-900 text-sm">
              {format(new Date(incident.started_at), 'PPpp')}
            </span>
          </div>
          {incident.resolved_at && (
            <div>
              <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Resolved</span>
              <span className="block mt-1 font-medium text-navy-900 text-sm">
                {format(new Date(incident.resolved_at), 'PPpp')}
              </span>
            </div>
          )}
        </div>

        {incident.components && incident.components.length > 0 && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2.5">Affected Components</h3>
            <div className="flex flex-wrap gap-2">
              {incident.components.map((component) => (
                <span
                  key={component.id}
                  className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-rose-50 text-rose-400"
                >
                  {component.display_name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-bold text-navy-900">Timeline</h2>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            {incident.updates?.map((update, index) => (
              <div key={update.id} className="relative pl-7">
                <div className={`absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full border-2 ${
                  index === 0 ? 'bg-navy-900 border-navy-900' : 'bg-white border-gray-300'
                }`} />
                {index < (incident.updates?.length || 0) - 1 && (
                  <div className="absolute left-[4.5px] top-4 bottom-0 w-px bg-gray-200" />
                )}
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    {update.state && (
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[11px] font-semibold rounded-md">
                        {update.state}
                      </span>
                    )}
                    <span className="text-[11px] text-gray-400">
                      {format(new Date(update.created_at), 'PPp')}
                    </span>
                  </div>
                  <p className="text-sm text-navy-900">{update.message}</p>
                  {update.author && (
                    <p className="text-[11px] text-gray-400 mt-1">by {update.author}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
